<script lang="ts" setup>
import BoxList from '~/components/lists/BoxList.vue'

const count = ref<number>(0)
const props = defineProps<{
  event: components['schemas']['Event']
  zone: components['schemas']['Zone']
}>()

const box = ref<components['schemas']['Box']>()
const toast = useToast()

const available = computed<number>(() => {
  if (props.event.status !== 'open') return 0
  if (parseFloat(props.zone.price) === 0) return 2
  if (props.zone.available < props.event.max_tickets_per_order)
    return props.zone.available
  if (props.zone.is_box) return 1
  return props.event.max_tickets_per_order
})

const actualPrice = computed(() =>
  parseFloat(props.zone.promo_price) ? props.zone.promo_price : props.zone.price
)

const actualDiscount = computed(() => {
  if (actualPrice.value === props.zone.price) return 0

  return 1 - parseFloat(actualPrice.value) / parseFloat(props.zone.price)
})

const onPurchase = () => {
  if (count.value <= 0) return
  if (props.zone.is_box && !box.value) {
    toast.add({
      life: 3000,
      severity: 'error',
      summary: 'Error',
      detail: 'Debes seleccionar un box',
    })
    return
  }

  const { intention } = usePurchase()

  intention.value = {
    ...intention.value,
    event: props.event,
    zone: props.zone,
    box: box.value,
    count: count.value,
  }

  navigateTo({ name: 'purchase' })
}

// const performersSchema = computed(() => {
//   if (props.event.performers?.length === 0) return {
//     '@type': 'Person',
//     '@id': `#performer-0`,
//     name: 'Otro'
//   }
//
//   const performer = props.event.performers[0]
//
//   return {
//     '@type': performer.type === 'solo' ? 'Person' : 'MusicGroup',
//     '@id': `#performer-${performer.id}`,
//     name: performer.name,
//     image: performer.image_portrait,
//     url: performer.website
//   }
// })

useSchemaOrg([
  defineEvent({
    '@id': `#event-${props.event.id}`,
    '@type': 'Event',
    name: props.event.name,
    description: props.event.description,
    eventAttendanceMode: 'OfflineEventAttendanceMode',
    eventStatus: 'EventScheduled',
    startDate: parseStringISO(props.event.date) ?? undefined,
    endDate: parseStringISO(props.event.close_at) ?? undefined,
    location: {
      '@type': 'Place',
      name: props.event.place,
      address: {
        '@type': 'PostalAddress',
        streetAddress: props.event.street_address,
        addressLocality: props.event.location.name,
        postalCode: props.event.location.province,
        addressRegion: props.event.location.region,
        addressCountry: 'PE',
      },
    },
    image: [props.event.image_hero, props.event.image_portrait],
    offers: {
      '@type': 'Offer',
      '@id': `#zone-${props.zone.id}`,
      name: props.zone.name,
      price: props.zone.price,
      priceCurrency: 'PEN',
      availability: available.value ? 'InStock' : 'SoldOut',
      priceValidUntil: parseStringISO(props.event.close_at),
      validFrom: parseStringISO(props.event.date_creation),
      url: `/events/${props.event.slug}`,
    },
    organizer: {
      name: 'Promotora MyM SRL',
      url: 'https://www.instagram.com/promotora.mym/',
    },
    // performer: performersSchema.value,
    remainingAttendeeCapacity:
      props.event.status === 'open' ? props.zone.available : 0,
    maximumAttendeeCapacity: props.zone.capacity,
  }),
])
</script>

<template>
  <Card class="col-span-2 lg:col-span-1">
    <template #title>{{ props.zone.name }}</template>
    <template #content>
      <div
        v-if="
          props.event.status === 'open' &&
          props.zone.available <= 10 &&
          props.zone.available > 0
        "
        class="mb-3"
      >
        <Message v-if="props.zone.is_box" severity="error">
          ¡Últimas entradas!
        </Message>
        <Message v-else severity="error">
          ¡Últimas {{ props.zone.available }} entradas!
        </Message>
      </div>
      <div class="flex items-center mb-3">
        <div class="grow">
          <div
            v-if="parseFloat(props.zone.promo_price) > 0"
            class="line-through text-red-400"
          >
            {{
              formatNumber(props.zone.price, 'S/ 0,0.00', { currency: 'PEN' })
            }}
          </div>
          <div
            class="font-semibold text-2xl"
            :class="{
              'text-blue-700': parseFloat(props.zone.promo_price) > 0,
              'mb-5': parseFloat(props.zone.promo_price) === 0,
            }"
          >
            {{ formatNumber(actualPrice, 'S/ 0,0.00', { currency: 'PEN' }) }}
          </div>
          <div
            v-if="parseFloat(props.zone.promo_price) > 0"
            class="font-semibold mb-5 text-surface-700"
          >
            {{ formatNumber(actualDiscount, '0%') }}
            de descuento
          </div>
          <div>
            <i class="mr-1 pi pi-ticket" />
            <span v-if="props.zone.is_box">Por box</span>
            <span v-else>Por persona</span>
          </div>
        </div>
        <div v-if="available">
          <div v-if="count > 0">
            <InputNumber
              v-model="count"
              :max="available"
              :min="0"
              :step="1"
              buttonLayout="horizontal"
              fluid
              decrementButtonClass="!bg-primary-500 !text-surface-0"
              incrementButtonClass="!bg-primary-500 !text-surface-0"
              showButtons
              style="width: 110px"
            >
              <template #incrementbuttonicon>
                <i class="pi pi-plus" />
              </template>
              <template #decrementbuttonicon>
                <i class="pi pi-minus" />
              </template>
            </InputNumber>
          </div>

          <Button v-else label="Comprar" size="large" @click="count++" />
        </div>

        <div v-else-if="props.event.status === 'open'">
          <Button disabled label="Agotado" severity="danger" size="large" />
        </div>
      </div>
    </template>
    <template #footer>
      <div v-if="count > 0" class="space-y-2">
        <ClientOnly>
          <BoxList
            v-if="props.zone.is_box && count === 1"
            :event="props.event"
            :selected-box="box"
            :zone="props.zone"
            @select="(value?: components['schemas']['Box']) => (box = value)"
          />
        </ClientOnly>
        <Button
          :label="`Comprar ${count} ${ props.zone.is_box ? 'box' : 'entradas'}`"
          fluid
          @click="onPurchase"
        />
      </div>
    </template>
  </Card>
</template>
